import { Activity, ArrowDown, ArrowUp, Minus, RefreshCw, ThumbsDown, ThumbsUp } from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { PostViewer } from "../components/PostViewer";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { useToast } from "../components/ui/use-toast";
import { useAuth } from "../contexts/AuthContext";
import { PlatformConnection } from "../models/Platform";
import { Post } from "../models/Post";

const ADMIN_EMAILS = ["lu@wavegen.ai", "lulu.li1989@gmail.com"];

interface TimePeriodMetrics {
  today: number;
  week: number;
  month: number;
  total: number;
  prevToday: number;
  prevWeek: number;
  prevMonth: number;
}

interface DailyBreakdown {
  date: string;
  generated: number;
  published: number;
  errors: number;
  newUsers: number;
}

interface DebugInfo {
  totalCustomersInDb: number;
  testUserCount: number;
  realCustomerCount: number;
  customersCollection: string;
  totalFeedbackInDb?: number;
  realFeedbackCount?: number;
}

interface FeedbackDailyBreakdown {
  date: string;
  up: number;
  down: number;
  withComment: number;
  total: number;
}

interface FeedbackPostSummary {
  id: string;
  status?: string;
}

interface FeedbackDetail {
  id: string;
  postId: string;
  userId: string;
  userEmail?: string | null;
  rating: "up" | "down";
  comment?: string;
  timestamp: number;
  date: string;
  post?: FeedbackPostSummary;
}

interface ErrorPost {
  id: string;
  date: string;
  type: "generation" | "publishing";
  error?: string;
}

interface WeeklyEngagement {
  week: string;
  signups: number;
  generated: number;
  published: number;
  exported: number;
}

interface RetentionRow {
  cohort: string;
  signups: number;
  weeks: Array<{ week: number; active: number; pct: number; avgCredits: number }>;
}

interface EngagementMetrics {
  weeklyEngagement: WeeklyEngagement[];
  retentionGrid: RetentionRow[];
  activatedUsers: TimePeriodMetrics;
}

interface AdminMetrics {
  posts: {
    generated: TimePeriodMetrics;
    published: TimePeriodMetrics;
    exported: TimePeriodMetrics;
    errors: TimePeriodMetrics;
  };
  users: {
    created: TimePeriodMetrics;
  };
  feedback?: {
    total: TimePeriodMetrics;
    up: TimePeriodMetrics;
    down: TimePeriodMetrics;
    withComment: TimePeriodMetrics;
    dailyBreakdown: FeedbackDailyBreakdown[];
    recentFeedbacks?: FeedbackDetail[];
  };
  activeUsers?: {
    generated: TimePeriodMetrics;
    published: TimePeriodMetrics;
    exported: TimePeriodMetrics;
    brandKitCreated?: TimePeriodMetrics;
    accountConnected?: TimePeriodMetrics;
    autopilotSetUp?: TimePeriodMetrics;
  };
  engagement?: EngagementMetrics;
  errorPosts: ErrorPost[];
  dailyBreakdown: DailyBreakdown[];
  debug?: DebugInfo;
}

function ComparisonBadge({ current, previous }: { current: number; previous: number }) {
  const diff = current - previous;
  if (diff === 0) {
    return (
      <span className="inline-flex items-center text-muted-foreground">
        <Minus className="h-3 w-3" />
      </span>
    );
  }
  const isPositive = diff > 0;
  return (
    <span className={`inline-flex items-center ${isPositive ? "text-green-600" : "text-red-600"}`}>
      {isPositive ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
      <span className="text-[10px] ml-0.5">{isPositive ? "+" : ""}{diff}</span>
    </span>
  );
}

function MetricsCard({
  title,
  description,
  metrics,
}: {
  title: string;
  description: string;
  metrics: TimePeriodMetrics;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{metrics.total.toLocaleString()}</div>
        <div className="grid grid-cols-3 gap-2 mt-3 text-xs text-muted-foreground">
          <div>
            <span className="font-medium text-foreground">{metrics.today}</span>{" "}
            <ComparisonBadge current={metrics.today} previous={metrics.prevToday} />
            <div className="text-[10px]">last 24hrs</div>
          </div>
          <div>
            <span className="font-medium text-foreground">{metrics.week}</span>{" "}
            <ComparisonBadge current={metrics.week} previous={metrics.prevWeek} />
            <div className="text-[10px]">last 7 days</div>
          </div>
          <div>
            <span className="font-medium text-foreground">{metrics.month}</span>{" "}
            <ComparisonBadge current={metrics.month} previous={metrics.prevMonth} />
            <div className="text-[10px]">last 30 days</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdminDashboard() {
  const { currentUser, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const [metrics, setMetrics] = useState<AdminMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPostForViewer, setSelectedPostForViewer] = useState<Post | null>(
    null
  );
  const [viewerConnectionsById, setViewerConnectionsById] = useState<
    Record<string, PlatformConnection>
  >({});
  const [viewerMediaUrls, setViewerMediaUrls] = useState<Record<string, string>>(
    {}
  );
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [openingPostId, setOpeningPostId] = useState<string | null>(null);

  const fetchMetrics = useCallback(async () => {
    if (!currentUser) return;

    setLoading(true);
    setError(null);

    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE}/api/admin/metrics`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error("Access denied - admin privileges required");
        }
        throw new Error("Failed to fetch metrics");
      }

      const data = await response.json();
      setMetrics(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser && !authLoading) {
      fetchMetrics();
    }
  }, [currentUser, authLoading, fetchMetrics]);

  const handleOpenPostViewer = useCallback(
    async (postId: string) => {
      if (!currentUser) return;

      setOpeningPostId(postId);
      try {
        const token = await currentUser.getIdToken();
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE}/api/admin/posts/${postId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch post details");
        }

        const data = (await response.json()) as {
          post: Post;
          connectionsById: Record<string, PlatformConnection>;
          mediaUrls?: Record<string, string>;
        };

        setSelectedPostForViewer(data.post);
        setViewerConnectionsById(data.connectionsById || {});
        setViewerMediaUrls(data.mediaUrls || {});
        setIsViewerOpen(true);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Could not open post viewer.";
        toast({
          title: "Error opening post",
          description: message,
          variant: "destructive",
        });
      } finally {
        setOpeningPostId(null);
      }
    },
    [currentUser, toast]
  );

  // Check if user is admin
  if (!authLoading && currentUser) {
    const isAdmin = ADMIN_EMAILS.includes(currentUser.email || "");
    if (!isAdmin) {
      return <Navigate to="/create-post" replace />;
    }
  }

  if (authLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  const feedback = metrics?.feedback;
  const feedbackTotal = feedback?.total.total ?? 0;
  const feedbackUp = feedback?.up.total ?? 0;
  const feedbackDown = feedback?.down.total ?? 0;
  const feedbackWithComment = feedback?.withComment.total ?? 0;
  const positivityRate =
    feedbackTotal > 0 ? Math.round((feedbackUp / feedbackTotal) * 100) : 0;
  const commentRate =
    feedbackTotal > 0 ? Math.round((feedbackWithComment / feedbackTotal) * 100) : 0;
  const recentFeedbacks = feedback?.recentFeedbacks ?? [];

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Admin Dashboard</h1>
          <p className="text-muted-foreground">
            Internal metrics (excluding test users)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild variant="outline" size="sm">
            <Link to="/admin/aeo">
              <Activity className="mr-2 h-4 w-4" />
              AEO visibility
            </Link>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchMetrics}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {error && (
        <div className="bg-destructive/10 text-destructive p-4 rounded-lg mb-6">
          {error}
        </div>
      )}

      {loading && !metrics ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Loading metrics...</div>
        </div>
      ) : metrics ? (
        <>
          {/* Debug Info */}
          {metrics.debug && (
            <div className="bg-muted/50 p-3 rounded-lg mb-4 text-xs text-muted-foreground">
              <span className="font-medium">Debug:</span>{" "}
              Collection: {metrics.debug.customersCollection} |{" "}
              Total in DB: {metrics.debug.totalCustomersInDb} |{" "}
              Test users: {metrics.debug.testUserCount} |{" "}
              Real customers: {metrics.debug.realCustomerCount}
              {typeof metrics.debug.realFeedbackCount === "number" &&
                ` | Real feedback: ${metrics.debug.realFeedbackCount}`}
            </div>
          )}

          {/* Summary Cards */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 mb-8">
            <MetricsCard
              title="Posts Generated"
              description="Total posts created"
              metrics={metrics.posts.generated}
            />
            <MetricsCard
              title="Posts Published"
              description="Successfully published"
              metrics={metrics.posts.published}
            />
            <MetricsCard
              title="Posts Exported"
              description="Downloaded by users (since Jan 31)"
              metrics={metrics.posts.exported}
            />
            <MetricsCard
              title="Users"
              description="Registered users"
              metrics={metrics.users.created}
            />
            {metrics.activeUsers && (
              <MetricsCard
                title="First-time Generators"
                description="Users by their first generated post"
                metrics={metrics.activeUsers.generated}
              />
            )}
            {metrics.activeUsers && (
              <MetricsCard
                title="First-time Publishers"
                description="Users by their first successful publish"
                metrics={metrics.activeUsers.published}
              />
            )}
            {metrics.activeUsers && (
              <MetricsCard
                title="Users Exported"
                description="Unique users who exported (since Jan 31)"
                metrics={metrics.activeUsers.exported}
              />
            )}
            {metrics.activeUsers?.brandKitCreated && (
              <MetricsCard
                title="Users Created Brand Kit"
                description="First-time brand kit creators"
                metrics={metrics.activeUsers.brandKitCreated}
              />
            )}
            {metrics.activeUsers?.accountConnected && (
              <MetricsCard
                title="Users Connected Account"
                description="First-time platform connectors"
                metrics={metrics.activeUsers.accountConnected}
              />
            )}
            {metrics.activeUsers?.autopilotSetUp && (
              <MetricsCard
                title="Users Set Up Autopilot"
                description="Unique users who configured autopilot"
                metrics={metrics.activeUsers.autopilotSetUp}
              />
            )}
            <MetricsCard
              title="Errors"
              description="Generation & publishing errors"
              metrics={metrics.posts.errors}
            />
            {metrics.engagement && (
              <MetricsCard
                title="Activated Users"
                description="Published or exported (union)"
                metrics={metrics.engagement.activatedUsers}
              />
            )}
          </div>

          {/* Engagement Metrics (Firestore-based) */}
          {metrics.engagement && (
            <div className="mb-8">
              {/* Weekly Activation Funnel */}
              <Card>
                <CardHeader>
                  <CardTitle>Weekly Activation Funnel</CardTitle>
                  <CardDescription>
                    Per signup cohort: how many generated, published, exported
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={metrics.engagement!.weeklyEngagement}
                        margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis
                          dataKey="week"
                          tick={{ fontSize: 11 }}
                          tickFormatter={(value: string) => {
                            const [, month, day] = value.split("-");
                            return `${parseInt(month)}/${parseInt(day)}`;
                          }}
                        />
                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "hsl(var(--card))",
                            border: "1px solid hsl(var(--border))",
                            borderRadius: "6px",
                            fontSize: "12px",
                          }}
                          labelFormatter={(value) => `Week of ${value}`}
                        />
                        <Legend wrapperStyle={{ fontSize: "12px" }} />
                        <Bar dataKey="signups" name="Signups" fill="hsl(221, 83%, 53%)" radius={[2, 2, 0, 0]} />
                        <Bar dataKey="generated" name="Generated" fill="hsl(var(--primary))" radius={[2, 2, 0, 0]} />
                        <Bar dataKey="published" name="Published" fill="hsl(142, 76%, 36%)" radius={[2, 2, 0, 0]} />
                        <Bar dataKey="exported" name="Exported" fill="hsl(35, 92%, 50%)" radius={[2, 2, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

            </div>
          )}

          {/* Retention Grid */}
          {metrics.engagement?.retentionGrid && (
            <Card className="mb-8">
              <CardHeader>
                <CardTitle>Retention Grid (Post Activity)</CardTitle>
                <CardDescription>
                  % of each signup cohort that created a post in week 0, 1, 2, etc. after signup
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-3 font-medium text-xs">Cohort</th>
                        <th className="text-right py-2 px-3 font-medium text-xs">Users</th>
                        {Array.from({ length: Math.max(0, ...metrics.engagement!.retentionGrid.map((r) => r.weeks.length)) }).map((_, i) => (
                          <th key={i} className="text-center py-2 px-3 font-medium text-xs whitespace-nowrap">
                            Wk {i}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {metrics.engagement!.retentionGrid.map((row) => (
                        <tr key={row.cohort} className="border-b last:border-0">
                          <td className="py-2 px-3 text-xs whitespace-nowrap">
                            {(() => {
                              const [, month, day] = row.cohort.split("-");
                              return `${parseInt(month)}/${parseInt(day)}`;
                            })()}
                          </td>
                          <td className="py-2 px-3 text-xs text-right text-muted-foreground">{row.signups}</td>
                          {row.weeks.map((w) => (
                            <td key={w.week} className="py-2 px-3 text-center">
                              <span
                                className="inline-block rounded px-2 py-0.5 text-xs font-medium"
                                style={{
                                  backgroundColor: `hsl(142, 76%, ${Math.max(95 - w.pct * 0.6, 36)}%)`,
                                  color: w.pct > 40 ? "white" : "hsl(142, 76%, 20%)",
                                }}
                              >
                                {w.pct}%
                              </span>
                              {w.avgCredits > 0 && (
                                <div className="text-[10px] text-muted-foreground mt-0.5">
                                  {w.avgCredits} cr/user
                                </div>
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Daily Breakdown Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Daily Breakdown (Last 30 Days)</CardTitle>
              <CardDescription>
                Posts generated, published, and errors by day
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[350px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={[...metrics.dailyBreakdown].reverse()}
                    margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 11 }}
                      tickFormatter={(value: string) => {
                        // Parse YYYY-MM-DD directly to avoid timezone shift
                        const [, month, day] = value.split("-");
                        return `${parseInt(month)}/${parseInt(day)}`;
                      }}
                      interval="preserveStartEnd"
                    />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "6px",
                        fontSize: "12px",
                      }}
                      labelFormatter={(value) => `Date: ${value}`}
                    />
                    <Legend wrapperStyle={{ fontSize: "12px" }} />
                    <Bar
                      dataKey="generated"
                      name="Generated"
                      fill="hsl(var(--primary))"
                      radius={[2, 2, 0, 0]}
                    />
                    <Bar
                      dataKey="published"
                      name="Published"
                      fill="hsl(142, 76%, 36%)"
                      radius={[2, 2, 0, 0]}
                    />
                    <Bar
                      dataKey="newUsers"
                      name="New Users"
                      fill="hsl(221, 83%, 53%)"
                      radius={[2, 2, 0, 0]}
                    />
                    <Bar
                      dataKey="errors"
                      name="Errors"
                      fill="hsl(var(--destructive))"
                      radius={[2, 2, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Post Feedback */}
          {feedback && (
            <>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 mt-8">
                <MetricsCard
                  title="Feedback Total"
                  description="Thumbs up + thumbs down"
                  metrics={feedback.total}
                />
                <MetricsCard
                  title="Thumbs Up"
                  description="Positive generation feedback"
                  metrics={feedback.up}
                />
                <MetricsCard
                  title="Thumbs Down"
                  description="Negative generation feedback"
                  metrics={feedback.down}
                />
                <MetricsCard
                  title="Feedback With Comment"
                  description="Ratings with written notes"
                  metrics={feedback.withComment}
                />
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Positivity Rate</CardTitle>
                    <CardDescription>Thumbs up share of all feedback</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{positivityRate}%</div>
                    <div className="mt-3 text-xs text-muted-foreground">
                      {feedbackUp.toLocaleString()} up, {feedbackDown.toLocaleString()} down
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Comment Rate</CardTitle>
                    <CardDescription>How often users leave context</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{commentRate}%</div>
                    <div className="mt-3 text-xs text-muted-foreground">
                      {feedbackWithComment.toLocaleString()} commented / {feedbackTotal.toLocaleString()} total
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card className="mt-8">
                <CardHeader>
                  <CardTitle>Post Feedback Trend (Last 30 Days)</CardTitle>
                  <CardDescription>
                    Daily thumbs up vs thumbs down from `post_feedbacks`
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[320px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={[...feedback.dailyBreakdown].reverse()}
                        margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis
                          dataKey="date"
                          tick={{ fontSize: 11 }}
                          tickFormatter={(value: string) => {
                            const [, month, day] = value.split("-");
                            return `${parseInt(month)}/${parseInt(day)}`;
                          }}
                          interval="preserveStartEnd"
                        />
                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "hsl(var(--card))",
                            border: "1px solid hsl(var(--border))",
                            borderRadius: "6px",
                            fontSize: "12px",
                          }}
                          labelFormatter={(value) => `Date: ${value}`}
                        />
                        <Legend wrapperStyle={{ fontSize: "12px" }} />
                        <Bar
                          dataKey="up"
                          name="Thumbs Up"
                          stackId="feedback"
                          fill="hsl(142, 76%, 36%)"
                          radius={[2, 2, 0, 0]}
                        />
                        <Bar
                          dataKey="down"
                          name="Thumbs Down"
                          stackId="feedback"
                          fill="hsl(var(--destructive))"
                          radius={[2, 2, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {recentFeedbacks.length > 0 && (
                <Card className="mt-8">
                  <CardHeader>
                    <CardTitle>Recent Feedback Details ({recentFeedbacks.length})</CardTitle>
                    <CardDescription>
                      Comments and related post previews
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-2 px-3 font-medium">Date</th>
                            <th className="text-left py-2 px-3 font-medium">User</th>
                            <th className="text-left py-2 px-3 font-medium">Rating</th>
                            <th className="text-left py-2 px-3 font-medium">Comment</th>
                            <th className="text-left py-2 px-3 font-medium">Post</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recentFeedbacks.map((item) => (
                            <tr key={item.id} className="border-b last:border-0 align-top">
                              <td className="py-2 px-3 whitespace-nowrap text-xs">
                                {new Date(item.timestamp).toLocaleString("en-US", {
                                  month: "numeric",
                                  day: "numeric",
                                  hour: "numeric",
                                  minute: "2-digit",
                                  hour12: true,
                                  timeZone: "America/Los_Angeles",
                                })}
                              </td>
                              <td className="py-2 px-3">
                                <div className="max-w-[220px]">
                                  <div className="text-xs break-all">
                                    {item.userEmail || "No email"}
                                  </div>
                                  {!item.userEmail && item.userId && (
                                    <div className="text-[10px] text-muted-foreground font-mono break-all mt-0.5">
                                      {item.userId}
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td className="py-2 px-3">
                                <span
                                  className={`inline-flex items-center justify-center rounded p-1.5 ${
                                    item.rating === "up"
                                      ? "bg-green-100 text-green-700"
                                      : "bg-red-100 text-red-700"
                                  }`}
                                  title={item.rating === "up" ? "Thumbs up" : "Thumbs down"}
                                >
                                  {item.rating === "up" ? (
                                    <ThumbsUp className="h-4 w-4" />
                                  ) : (
                                    <ThumbsDown className="h-4 w-4" />
                                  )}
                                </span>
                              </td>
                              <td className="py-2 px-3 text-xs text-muted-foreground max-w-sm whitespace-pre-wrap">
                                {item.comment || "No comment"}
                              </td>
                              <td className="py-2 px-3">
                                {item.postId ? (
                                  <div className="flex flex-col gap-1">
                                    <span className="font-mono text-xs">{item.postId}</span>
                                    <button
                                      type="button"
                                      onClick={() => handleOpenPostViewer(item.postId)}
                                      disabled={openingPostId === item.postId}
                                      className="text-left text-xs text-blue-600 hover:underline disabled:text-muted-foreground disabled:no-underline"
                                    >
                                      {openingPostId === item.postId
                                        ? "Opening..."
                                        : "Open in PostViewer"}
                                    </button>
                                    {item.post?.status && (
                                      <span className="text-[10px] text-muted-foreground">
                                        {item.post.status}
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-xs text-muted-foreground">Unknown post</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {/* Error Posts List */}
          {metrics.errorPosts.length > 0 && (
            <Card className="mt-8">
              <CardHeader>
                <CardTitle>Error Posts ({metrics.errorPosts.length})</CardTitle>
                <CardDescription>
                  Posts with generation or publishing errors
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-3 font-medium">Post ID</th>
                        <th className="text-left py-2 px-3 font-medium">Date</th>
                        <th className="text-left py-2 px-3 font-medium">Type</th>
                        <th className="text-left py-2 px-3 font-medium">Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {metrics.errorPosts
                        .sort((a, b) => b.date.localeCompare(a.date))
                        .map((post, idx) => (
                          <tr key={`${post.id}-${idx}`} className="border-b last:border-0">
                            <td className="py-2 px-3 font-mono text-xs">{post.id}</td>
                            <td className="py-2 px-3">{post.date}</td>
                            <td className="py-2 px-3">
                              <span
                                className={`px-2 py-0.5 rounded text-xs ${
                                  post.type === "generation"
                                    ? "bg-orange-100 text-orange-700"
                                    : "bg-red-100 text-red-700"
                                }`}
                              >
                                {post.type}
                              </span>
                            </td>
                            <td className="py-2 px-3 text-xs text-muted-foreground max-w-xs truncate">
                              {post.error}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {isViewerOpen && selectedPostForViewer && (
            <PostViewer
              isOpen={isViewerOpen}
              onClose={() => {
                setIsViewerOpen(false);
                setSelectedPostForViewer(null);
                setViewerConnectionsById({});
                setViewerMediaUrls({});
              }}
              expandedPost={selectedPostForViewer}
              connectionsById={viewerConnectionsById}
              mediaUrls={viewerMediaUrls}
              initialIndex={0}
              enableLiveUpdates={false}
              readOnly
            />
          )}
        </>
      ) : null}
    </div>
  );
}
